import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, UploadCloud, FileText } from "lucide-react";
import { toast } from "sonner";
import { PageHead, apiErr, fmtDate, useMerendaMeta } from "@/merenda/common";
import { InvoiceReview } from "@/merenda/InvoiceReview";

const ST = { CONFERENCIA: "bg-amber-100 text-amber-800", REGISTRADA: "bg-green-100 text-green-700", DESCARTADA: "bg-slate-200 text-slate-600" };

export default function Notas() {
  const { meta, foods, scopeParams } = useMerendaMeta();
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/merenda/invoices", { params: scopeParams }).then((r) => setList(r.data));
  useEffect(() => { if (meta) load(); }, [meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (scopeParams.school_id) fd.append("school_id", scopeParams.school_id);
      const { data } = await api.post("/merenda/invoices", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Nota enviada — confira os dados antes de registrar a entrada");
      await load(); setOpen(data);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  if (!meta || !list) return <div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  return (
    <div data-testid="notas-page">
      <PageHead title="Notas fiscais e entradas" subtitle="Envie o XML da NF-e (leitura automática) ou PDF/fotos. Várias páginas no mesmo envio = um único documento.">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600" data-testid="notas-upload-label">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Enviar nota
          <input type="file" multiple accept=".xml,.pdf,.png,.jpg,.jpeg,.webp,.heic" className="hidden" onChange={upload} disabled={busy} data-testid="notas-file-input" />
        </label>
      </PageHead>
      <p className="mb-4 rounded-lg bg-blue-50 p-3 text-xs text-blue-800">XML é lido na hora. Para PDF/fotos, abra a nota e use “Ler PDF/foto com IA” — os dados sempre passam pela sua conferência antes de entrar no estoque.</p>
      <div className="grid gap-3">
        {list.map((n) => (
          <Card key={n.id} className="flex cursor-pointer flex-wrap items-center gap-3 border-slate-200 p-4 hover:border-orange-300" onClick={() => setOpen(n)} data-testid={`nota-card-${n.id}`}>
            <FileText className="h-8 w-8 text-slate-400" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-800">{n.supplier || "Fornecedor não informado"} · NF {n.number || "—"}{n.series ? `/${n.series}` : ""}</div>
              <div className="text-xs text-slate-500">Emissão {fmtDate(n.issue_date)} · Recebimento {fmtDate(n.received_date)} · {n.items.length} item(ns) · {n.files.length} arquivo(s)</div>
            </div>
            {n.duplicates?.length > 0 && n.status === "CONFERENCIA" && <Badge className="bg-red-100 text-red-700">Possível duplicidade</Badge>}
            <Badge className={ST[n.status]}>{n.status === "CONFERENCIA" ? "Aguardando conferência" : n.status === "REGISTRADA" ? "Registrada" : "Descartada"}</Badge>
          </Card>
        ))}
        {!list.length && <Card className="border-dashed p-10 text-center text-slate-400">Nenhuma nota enviada.</Card>}
      </div>
      {open && <InvoiceReview inv={open} foods={foods} scopeParams={scopeParams} onClose={() => setOpen(null)} onChange={load} />}
    </div>
  );
}
